import { apiClient } from '@api/api-client'
import {
  clearRequestOrganizationId,
  setRequestOrganizationId,
} from '@api/request-correlation'
import {
  clearAuthenticatedLogContext,
  setAuthenticatedLogContext,
} from '@config/logging/log-transport'
import { useClearActiveUser } from '@store/view-as-user'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { AuthContext } from './auth-context'
import { setGlobal401Handler } from './auth-error-handler'
import { hashUsername } from './hash-username'
import { mapUserResponse } from './map-user-response'

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const refreshTimer = useRef(null)
  const orgIdRef = useRef(null)
  const logUserRef = useRef({ userHash: null, orgId: null })
  const clearActiveUser = useClearActiveUser()

  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState({
    userName: null,
    userRole: null,
    initialized: false,
    orgId: null,
    orgName: null,
    billPay: false,
  })

  useEffect(() => {
    if (!user.initialized) return

    if (orgIdRef.current && orgIdRef.current !== user.orgId) {
      queryClient.clear()
    }

    orgIdRef.current = user.orgId
  }, [user.orgId, user.initialized, queryClient])

  const expireUser = useCallback(() => {
    setUser({
      userName: null,
      userRole: null,
      initialized: true,
      orgId: null,
      orgName: null,
      billPay: false,
    })

    queryClient.getQueryCache().clear()
    clearActiveUser()
    logUserRef.current = { userHash: null, orgId: null }
    clearRequestOrganizationId()
    clearAuthenticatedLogContext()
  }, [queryClient, clearActiveUser])

  const loadUser = useCallback(
    async ({ throwOnError = false } = {}) => {
      try {
        setLoading(true)

        const response = await apiClient.get('/auth/users/me')
        setRequestOrganizationId(response.organization_id)

        const user = mapUserResponse(response)

        setUser(user)
        setLoading(false)

        try {
          const userHash = await hashUsername(response.username)
          const orgId = response.organization_id
          const prev = logUserRef.current
          const changed = prev.userHash !== userHash || prev.orgId !== orgId

          if (userHash && orgId && changed) {
            setAuthenticatedLogContext(userHash, orgId)
            logUserRef.current = { userHash, orgId }
          }
        } catch {
          // ignore telemetry errors
        }
        return user
      } catch (error) {
        expireUser()
        if (throwOnError) {
          throw error
        }
        return null
      } finally {
        setLoading(false)
      }
    },
    [expireUser],
  )

  const logout = useCallback(async () => {
    try {
      setLoading(true)

      await apiClient.post('/auth/logout')

      setUser({
        userName: null,
        userRole: null,
        initialized: false,
        orgId: null,
        orgName: null,
        billPay: false,
      })

      queryClient.getQueryCache().clear()
      clearActiveUser()
      logUserRef.current = { userHash: null, orgId: null }
      clearRequestOrganizationId()
      clearAuthenticatedLogContext()

      navigate('/signin')
    } catch {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }, [queryClient, clearActiveUser, navigate])

  useEffect(() => {
    setGlobal401Handler(expireUser)
  }, [expireUser])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  useEffect(
    () => () => {
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current)
      }
    },
    [],
  )

  const value = {
    ...user,
    loading,
    setLoading,
    setUser,
    loadUser,
    expireUser,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
